import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
  Typography, Tag, Spin, Button, Card, message, Select, Space, Input, Segmented, Tooltip,
  Row, Col, Divider, Avatar, Popconfirm, DatePicker, Collapse, Table, Form, Checkbox, Modal, theme,
} from 'antd';
import {
  UserOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';
import CommentBox, { CommentAttachments } from '../components/CommentBox';
import useHelpdeskPulse from '../components/useHelpdeskPulse';
import LinkifiedText from '../components/LinkifiedText';
import HelpdeskCreateModal from '../components/HelpdeskCreateModal';
import { ticketKey } from '../components/HelpdeskInvolvedTickets';
import TicketAssetsCard from '../components/TicketAssetsCard';
import TicketTasksCard from '../components/TicketTasksCard';
import './HelpdeskTicketPage.css';
import HelpdeskFormQuestions, { answersToPayload, answersToValues, answerText } from '../components/HelpdeskFormQuestions';
import { HdStatusTag, statusLabel, isDoneStatus, useHelpdeskStatuses } from '../components/HelpdeskStatus';
import { useBackFallback, usePageLabel } from '../components/BackChip';

// The Status picker: the ticket's status, then the next steps its Request
// Type's workflow draws from it (Admin > Help Desk Workflows), named by the
// arrow's label when it has one, then the workflow's other statuses. The arrows
// guide; any of them can be picked.
function statusOptions(ticket) {
  const wf = ticket.Workflow || { next: [], statuses: [] };
  const opt = (k) => ({ value: k, label: statusLabel(k) });
  const step = (k) => (wf.labels?.[k] ? { value: k, label: `${wf.labels[k]} → ${statusLabel(k)}` } : opt(k));
  const rest = wf.statuses.filter((k) => k !== ticket.Status && !wf.next.includes(k));
  return [
    opt(ticket.Status),
    ...(wf.next.length ? [{ label: 'Next steps', options: wf.next.map(step) }] : []),
    ...(rest.length ? [{ label: 'Other statuses', options: rest.map(opt) }] : []),
  ];
}

export default function HelpdeskTicketPage() {
  useHelpdeskStatuses();   // re-render with the statuses' names once read
  const { id }      = useParams();
  const currentUser = useContext(UserContext);
  const { token } = theme.useToken();

  const [ticket,        setTicket]        = useState(null);
  // Names this page in Recent and on the next page's Back chip; the Back
  // chip (Layout) goes where you came from, else the Help Desk.
  const subj = ticket?.Subject || '';
  usePageLabel(ticket ? `Ticket ${ticketKey(ticket)}: ${subj.length > 60 ? `${subj.slice(0, 57)}...` : subj}` : null);
  useBackFallback('/helpdesk', 'Help Desk');
  // The person's (or client's) other tickets, and the employee's shipping
  // history (every ticket about them, this one too): null until their panel
  // is first opened, so the page itself does not wait on them.
  const [history,       setHistory]       = useState(null);
  const [shipHistory,   setShipHistory]   = useState(null);
  const [historyView,   setHistoryView]   = useState('tickets');   // tickets | shipments
  const [historyOpen,   setHistoryOpen]   = useState([]);
  const [clientHistoryOpen, setClientHistoryOpen] = useState([]);
  const historyRef = useRef(null);
  const [assets,        setAssets]        = useState([]);
  // Request Type form: its questions, the saved answers, and edit mode.
  const [formDef,       setFormDef]       = useState(null);
  const [answers,       setAnswers]       = useState([]);
  const [editingAnswers, setEditingAnswers] = useState(false);
  // Title (Subject) and Description, edited together on the Description card.
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsSaving,  setDetailsSaving]  = useState(false);
  const [detailsForm] = Form.useForm();
  // internal (queue only) | public | email (public, emailed to the employee, watchers CC'd)
  const [commentMode,   setCommentMode]   = useState(null);
  const [answersSaving, setAnswersSaving] = useState(false);
  const [answersForm] = Form.useForm();
  const [assetTypes,    setAssetTypes]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [comments,      setComments]      = useState([]);
  const [watchers,      setWatchers]      = useState([]);
  const [emailTo,       setEmailTo]       = useState(null);
  // Who a public comment notifies (and who of them gets an email).
  const [notifyTo,      setNotifyTo]      = useState(null);
  // Send email only: sign the email with my name (on by default).
  const [includeSignature, setIncludeSignature] = useState(true);
  // Hide system notes (status, assignee, asset changes) in Comments. On by
  // default; turning it off is remembered in this browser (a convenience only,
  // so storage failing is harmless).
  const [hideSystem,    setHideSystem]    = useState(() => {
    try { return localStorage.getItem('helpdesk.hideSystemComments') !== '0'; } catch { return true; }
  });
  const toggleHideSystem = (on) => {
    setHideSystem(on);
    try { localStorage.setItem('helpdesk.hideSystemComments', on ? '1' : '0'); } catch { /* ignore */ }
  };
  const [followUpOpen,  setFollowUpOpen]  = useState(false);
  const [filterOpts,    setFilterOpts]    = useState({ teams: [], users: [] });
  const [addingComment, setAddingComment] = useState(false);

  const [statusSaving,  setStatusSaving]  = useState(false);
  const [dueSaving,     setDueSaving]     = useState(false);
  const [addingWatcher, setAddingWatcher] = useState(false);
  const [watcherUserId, setWatcherUserId] = useState(null);
  const [assignSaving,  setAssignSaving]  = useState(false);
  const [impactedSaving, setImpactedSaving] = useState(false);
  const [addingDue,     setAddingDue]     = useState(false);

  const loadAssets = useCallback(() => {
    api.get(`/api/helpdesk/${id}/assets`).then((r) => setAssets(r.data || [])).catch(() => {});
  }, [id]);
  useEffect(() => { loadAssets(); }, [loadAssets]);
  useEffect(() => {
    api.get('/api/helpdesk/asset-types', { params: { active_only: 1 } })
      .then((r) => setAssetTypes(r.data || [])).catch(() => {});
  }, []);
  // An asset change adds a comment, so the comments are reloaded with it.
  const onAssetsChanged = () => { loadAssets(); loadComments(); setShipHistory(null); };

  const ticketFormId = ticket?.FormID;
  const loadAnswers = useCallback(() => {
    api.get(`/api/helpdesk/${id}/answers`).then((r) => setAnswers(r.data || [])).catch(() => {});
  }, [id]);
  useEffect(() => {
    if (!ticketFormId) { setFormDef(null); return; }
    api.get(`/api/eval-forms/${ticketFormId}`).then((r) => setFormDef(r.data)).catch(() => {});
    loadAnswers();
  }, [ticketFormId, loadAnswers]);

  const startEditAnswers = () => {
    answersForm.setFieldsValue({ answers: answersToValues(formDef, answers) });
    setEditingAnswers(true);
  };
  const saveAnswers = async () => {
    let v;
    try { v = await answersForm.validateFields(); } catch { return; }
    setAnswersSaving(true);
    try {
      await api.put(`/api/helpdesk/${id}/answers`, { answers: answersToPayload(formDef, v.answers) });
      setEditingAnswers(false);
      loadAnswers();
      loadComments();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setAnswersSaving(false);
    }
  };

  const startEditDetails = () => {
    detailsForm.setFieldsValue({ subject: ticket.Subject, description: ticket.Description || '' });
    setEditingDetails(true);
  };
  const saveDetails = async () => {
    let v;
    try { v = await detailsForm.validateFields(); } catch { return; }
    setDetailsSaving(true);
    try {
      await api.patch(`/api/helpdesk/${id}/details`, { subject: v.subject, description: v.description || null });
      const tRes = await api.get(`/api/helpdesk/${id}`);
      setTicket(tRes.data.ticket);
      setEditingDetails(false);
      loadComments();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setDetailsSaving(false);
    }
  };

  // The other tickets of the person this ticket is about -- or, on a client
  // ticket, the client's other tickets (Client Ticket History).
  const historyUserId = ticket?.ApplicableUserID || ticket?.AgentUserID;
  const historyClientId = ticket?.ClientID || null;
  useEffect(() => { setHistory(null); setShipHistory(null); }, [historyUserId, historyClientId, id]);
  const wantHistory = clientHistoryOpen.length > 0 || (historyOpen.length > 0 && historyView === 'tickets');
  useEffect(() => {
    if (!wantHistory || history !== null) return;
    if (!historyUserId && !historyClientId) { setHistory([]); return; }
    const params = historyClientId ? { client_ids: historyClientId } : { employee_user_id: historyUserId };
    api.get('/api/helpdesk', { params })
      // The other tickets: the one open here is left out.
      .then((r) => setHistory((r.data || []).filter((t) => String(t.ID) !== String(id))))
      .catch(() => setHistory([]));
  }, [wantHistory, history, historyUserId, historyClientId, id]);
  const wantShipHistory = historyOpen.length > 0 && historyView === 'shipments';
  useEffect(() => {
    if (!wantShipHistory || shipHistory !== null) return;
    if (!historyUserId) { setShipHistory([]); return; }
    api.get('/api/helpdesk/employee-shipments', { params: { employee_user_id: historyUserId } })
      .then((r) => setShipHistory(r.data || []))
      .catch(() => setShipHistory([]));
  }, [wantShipHistory, shipHistory, historyUserId]);

  // The Shipments panel's link: open the history panel on Shipments.
  const showShipHistory = () => {
    setHistoryView('shipments');
    setHistoryOpen(['history']);
    setTimeout(() => historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const loadComments = useCallback(async () => {
    try {
      const r = await api.get(`/api/helpdesk/${id}/comments`);
      setComments(r.data);
    } catch { /* silent */ }
  }, [id]);

  // When the ticket or its comments change -- someone else assigns it,
  // changes the status or comments -- they reload here, quietly; how soon is
  // the ticket's queue's setting (useHelpdeskPulse: seconds for IT). Someone
  // else's new comment also says so. A comment being typed is not touched;
  // edits here save at once, so nothing is lost. The ticket is left alone
  // while Subject/Description or the answers are being edited, or a change is
  // saving, so nothing typed or on its way is overwritten; the next change
  // catches up.
  const busyRef = useRef(false);
  busyRef.current = editingDetails || editingAnswers || detailsSaving || answersSaving
    || statusSaving || assignSaving || dueSaving;
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  useHelpdeskPulse(`/api/helpdesk/${id}/pulse`, async () => {
    const seen = new Set(commentsRef.current.map((c) => c.ID));
    try {
      const r = await api.get(`/api/helpdesk/${id}/comments`);
      setComments(r.data);
      r.data.filter((c) => !seen.has(c.ID) && c.UserID && c.UserID !== currentUser?.ID && c.AuthorName !== 'System')
        .forEach((c) => message.info(`${c.AuthorName} commented`));
    } catch { /* the next change tries again */ }
    if (busyRef.current) return;
    api.get(`/api/helpdesk/${id}`)
      .then((r) => { if (r.data?.ticket && !busyRef.current) setTicket(r.data.ticket); })
      .catch(() => {});
  });

  // The Request Type's self-service solutions, for queue members to insert
  // into a comment -- every one, whether or not it shows on New Ticket.
  const [solutionOpts, setSolutionOpts] = useState([]);
  const ticketFormIdForSolutions = ticket?.FormID;
  const ticketSeesInternal = !!ticket?.SeesInternal;
  useEffect(() => {
    if (!ticketFormIdForSolutions || !ticketSeesInternal) { setSolutionOpts([]); return; }
    api.get(`/api/helpdesk/forms/${ticketFormIdForSolutions}/solutions`, { params: { all: 1 } })
      .then((r) => setSolutionOpts((r.data || []).map((x) => ({
        key: x.ID,
        label: x.Title,
        text: [x.Title, x.Steps, x.URL].filter((v) => v && String(v).trim()).join('\n'),
      }))))
      .catch(() => setSolutionOpts([]));
  }, [ticketFormIdForSolutions, ticketSeesInternal]);

  const loadWatchers = useCallback(async () => {
    try {
      const r = await api.get(`/api/helpdesk/${id}/watchers`);
      setWatchers(r.data);
    } catch { /* silent */ }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    // Creating a follow-up navigates here with the new ticket's id.
    setFollowUpOpen(false);
    Promise.all([
      api.get(`/api/helpdesk/${id}`),
      api.get(`/api/helpdesk/${id}/comments`),
      api.get(`/api/helpdesk/${id}/watchers`),
      api.get('/api/helpdesk/filter-options'),
    ])
      .then(([tRes, cRes, wRes, fRes]) => {
        setTicket(tRes.data.ticket);
        setComments(cRes.data);
        setWatchers(wRes.data);
        setFilterOpts(fRes.data);
      })
      .catch(() => message.error('Failed to load ticket.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    setStatusSaving(true);
    try {
      await api.put(`/api/helpdesk/${id}/status`, { status: newStatus });
      // Reload: the workflow's next steps follow the new status.
      api.get(`/api/helpdesk/${id}`).then((r) => setTicket(r.data.ticket)).catch(() => setTicket((t) => ({ ...t, Status: newStatus })));
      await loadComments();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleAssign = async (userId) => {
    setAssignSaving(true);
    try {
      // Sends who this page shows as the assignee: if someone else has taken
      // the ticket since, the server says who instead of overriding them.
      await api.patch(`/api/helpdesk/${id}/assign`, {
        assigned_to_user_id: userId || null,
        expected_assigned_to_user_id: ticket.AssignedToUserID || null,
      });
      const [tRes] = await Promise.all([api.get(`/api/helpdesk/${id}`), loadComments()]);
      setTicket(tRes.data.ticket);
    } catch (err) {
      if (err.response?.status === 409) {
        message.warning(err.response.data.error);
        const [tRes] = await Promise.all([api.get(`/api/helpdesk/${id}`), loadComments()]);
        setTicket(tRes.data.ticket);
      } else {
        message.error(err.response?.data?.error || 'Failed to update the assignee');
      }
    } finally {
      setAssignSaving(false);
    }
  };

  // Who the ticket is about: the Impacted Employee, or on an Applicable
  // Employee ticket (onboarding, offboarding) the Applicable Employee.
  const handleImpactedChange = async (userId) => {
    setImpactedSaving(true);
    try {
      if (ticket.ApplicableUserID) await api.patch(`/api/helpdesk/${id}/applicable`, { applicable_user_id: userId });
      else await api.patch(`/api/helpdesk/${id}/impacted`, { agent_user_id: userId });
      const [tRes] = await Promise.all([api.get(`/api/helpdesk/${id}`), loadComments()]);
      setTicket(tRes.data.ticket);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to change the employee');
    } finally {
      setImpactedSaving(false);
    }
  };

  const handleDueDateChange = async (date) => {
    setDueSaving(true);
    try {
      const due_date = date ? date.format('YYYY-MM-DD') : null;
      await api.patch(`/api/helpdesk/${id}/due-date`, { due_date });
      setTicket((t) => ({ ...t, DueDate: due_date }));
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update due date');
    } finally {
      setDueSaving(false);
    }
  };

  // The Impacted Employee closes their own ticket ("it's working now"), with
  // an optional comment.
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeNote, setCloseNote] = useState('');
  const [closing, setClosing] = useState(false);
  const handleCloseTicket = async () => {
    setClosing(true);
    try {
      await api.post(`/api/helpdesk/${id}/close`, { comment: closeNote });
      const tRes = await api.get(`/api/helpdesk/${id}`);
      setTicket(tRes.data.ticket);
      loadComments();
      setCloseOpen(false);
      setCloseNote('');
      message.success('Ticket closed.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to close.');
    } finally {
      setClosing(false);
    }
  };

  const handleCancelRequest = async () => {
    try {
      await api.post(`/api/helpdesk/${id}/cancel`);
      const tRes = await api.get(`/api/helpdesk/${id}`);
      setTicket(tRes.data.ticket);
      loadComments();
      message.success('Request canceled.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to cancel.');
    }
  };

  const toggleNotifyImpacted = (on) => {
    api.put(`/api/helpdesk/${id}/notify-impacted`, { on })
      .then(() => { setTicket((t) => ({ ...t, NotifyImpacted: on ? 1 : 0 })); loadComments(); })
      .catch((err) => message.error(err.response?.data?.error || 'Failed to update.'));
  };

  // Who Send email would go to, refreshed when the watchers change.
  useEffect(() => {
    if (commentMode !== 'email') { setEmailTo(null); return; }
    api.get(`/api/helpdesk/${id}/email-recipients`)
      .then((r) => setEmailTo(r.data))
      .catch(() => setEmailTo(null));
  }, [commentMode, id, watchers]);

  useEffect(() => {
    if ((commentMode || 'public') !== 'public') { setNotifyTo(null); return; }
    api.get(`/api/helpdesk/${id}/comment-recipients`)
      .then((r) => setNotifyTo(r.data.recipients || []))
      .catch(() => setNotifyTo(null));
  }, [commentMode, id, watchers, ticket?.Status, ticket?.AgentUserID, ticket?.NotifyImpacted]);

  const handleCommentSubmit = async (text, files) => {
    setAddingComment(true);
    try {
      const mode = commentMode || 'public';
      const res = await api.post(`/api/helpdesk/${id}/comments`, {
        comment: text, mode,
        ...(mode === 'email' ? { include_signature: includeSignature } : {}),
      });
      const commentId = res.data.id;
      if (mode === 'email') {
        if (res.data.emailed) message.success(`Emailed — ${res.data.emailed_to}`);
        else message.warning(res.data.warning || 'The comment was posted, but no email was sent.');
      }
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f.file, f.name);
        fd.append('comment_id',   commentId);
        fd.append('comment_type', 'helpdesk');
        await api.post('/api/attachments', fd);
      }
      // The workflow's arrows may have moved the status (the queue's reply, the
      // customer's); show it without a reload.
      if (res.data.status_changed) {
        const tRes = await api.get(`/api/helpdesk/${id}`);
        setTicket(tRes.data.ticket);
      }
      await loadComments();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const handleAddWatcher = async () => {
    if (!watcherUserId) return;
    setAddingWatcher(true);
    try {
      await api.post(`/api/helpdesk/${id}/watchers`, { user_id: watcherUserId });
      setWatcherUserId(null);
      await loadWatchers();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add watcher');
    } finally {
      setAddingWatcher(false);
    }
  };

  const handleRemoveWatcher = async (userId) => {
    try {
      await api.delete(`/api/helpdesk/${id}/watchers/${userId}`);
      await loadWatchers();
    } catch {
      message.error('Failed to remove watcher');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: 80 }}><Spin size="large" /></div>;
  if (!ticket) return <Typography.Text type="danger">Ticket not found.</Typography.Text>;

  const watcherIds   = new Set(watchers.map((w) => w.UserID));
  // Anyone can be a watcher except the Applicable Employee. A client's own
  // login picks from its company's logins only (from the server).
  const watcherOpts  = (currentUser?.Role === 'client' ? (ticket.ClientWatcherOptions || []) : (filterOpts.users || []))
    .filter((u) => u.ID !== ticket.ApplicableUserID)
    // Client logins watch Client Ticket Type tickets only; other View Only
    // users never.
    .filter((u) => (u.Role === 'client' ? !!ticket.IsClientTicket : !u.IsViewOnly))
    .filter((u) => !watcherIds.has(u.ID))
    .map((u) => ({ value: u.ID, label: u.DisplayName }));

  // The panel's employee: the Impacted Employee, or on an Applicable Employee
  // ticket the Applicable Employee (the person being onboarded or offboarded;
  // the Impacted Employee there is whoever raised it, and gets the updates).
  // Choices: active users (clients on client tickets only), never the other
  // of the two, nor a watcher for the Applicable Employee; the current one
  // stays listed even if inactive.
  const aboutApplicable = !!ticket.ApplicableUserID;
  const aboutId = aboutApplicable ? ticket.ApplicableUserID : ticket.AgentUserID;
  const impactedOpts = (filterOpts.users || [])
    .filter((u) => u.ID !== (aboutApplicable ? ticket.AgentUserID : ticket.ApplicableUserID))
    .filter((u) => !aboutApplicable || !watchers.some((w) => w.UserID === u.ID))
    .filter((u) => (u.Role === 'client' ? !!ticket.IsClientTicket : true))
    .map((u) => ({ value: u.ID, label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName }));
  if (aboutId && !impactedOpts.some((o) => o.value === aboutId)) {
    impactedOpts.unshift({ value: aboutId, label: (aboutApplicable ? ticket.ApplicableName : ticket.AgentName) || 'Unknown' });
  }

  // Assignee choices: the queue's active members. The current assignee stays
  // listed even after leaving the queue or being deactivated, so the field
  // never goes blank on its own; they remain until someone picks another.
  const queueMembers   = ticket.QueueMembers || [];
  const assigneeOpts   = queueMembers.map((u) => ({ value: u.ID, label: u.DisplayName }));
  if (ticket.AssignedToUserID && !queueMembers.some((u) => u.ID === ticket.AssignedToUserID)) {
    assigneeOpts.unshift({
      value: ticket.AssignedToUserID,
      label: `${ticket.AssignedToName || 'Unknown'} (${ticket.AssignedToIsActive === false ? 'inactive' : 'not in queue'})`,
    });
  }
  const inQueueTeam    = queueMembers.some((u) => u.ID === currentUser?.ID);

  // Only the ticket's queue members change it; anyone else views it and adds
  // public comments. The server enforces the same rule.
  const canChange = !!ticket.IsQueueMember;

  // The Request Type's questions, answered or not (shown in Details).
  const questions = (formDef?.Sections || []).flatMap((sec) => sec.Questions || []);

  const isOverdue = ticket.DueDate && !isDoneStatus(ticket.Status)
    && dayjs(ticket.DueDate).isBefore(dayjs(), 'day');

  const who = ticket.ApplicableUserID ? 'Applicable' : 'Impacted';
  const counted = (name, rows) => (rows ? `${name} (${rows.length})` : name);
  const day = (v) => formatTS(v, 'YYYY-MM-DD');

  return (
    <div className="hd-ticket">
      <Row gutter={24} align="top">
        {/* Left, two thirds: what the ticket is about and the conversation */}
        <Col xs={24} md={14} lg={16}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space size={6} style={{ marginBottom: 6 }}>
              <span>
                <Typography.Text type="secondary" style={{ fontSize: 16 }}>Ticket ID: </Typography.Text>
                <Typography.Text style={{ fontSize: 16, fontWeight: 600 }} copyable={{ text: ticketKey(ticket) }}>
                  {ticketKey(ticket)}
                </Typography.Text>
              </span>
              {/* No status tag: the Status dropdown below shows it. */}
              {/* The impacted employee can cancel their own open request --
                  not on a Request Type only the queue closes. */}
              {ticket.CanCloseAsImpacted && (
                <Popconfirm
                  title="Cancel this request?"
                  description="The ticket will be set to Canceled."
                  okText="Cancel Request"
                  okButtonProps={{ danger: true }}
                  cancelText="Keep It"
                  onConfirm={handleCancelRequest}
                >
                  <Button size="small" danger>Cancel Request</Button>
                </Popconfirm>
              )}
              {ticket.CanCloseAsImpacted && (
                <Button size="small" type="primary" onClick={() => setCloseOpen(true)}>Close Ticket</Button>
              )}
              {canChange && (
                <Button size="small" onClick={() => setFollowUpOpen(true)}>Create Follow-up</Button>
              )}
            </Space>
            <Typography.Title level={3} style={{ margin: '4px 0 6px' }}>
              {ticket.FormName ? `${ticket.FormName} - ${ticket.Subject}` : ticket.Subject}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Created by {ticket.CreatedByName} &middot; {formatTS(ticket.CreatedAt)}
              {ticket.DueDate && (
                <span style={{ marginLeft: 12, color: isOverdue ? '#ff4d4f' : undefined }}>
                  Due: <strong>{ticket.DueDate}</strong>
                  {isOverdue && ' (overdue)'}
                </span>
              )}
            </Typography.Text>
          </div>
        </div>

        {/* Who the ticket is for, under the title and just above Status, so
            it is read with everything else: the Applicable Employee when
            there is one (they never see the ticket). */}
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 4 }}>
          {ticket.ApplicableUserID && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Applicable Employee</Typography.Text>
              <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{ticket.ApplicableName || '—'}</div>
            </div>
          )}
          {/* Hidden when there is an Applicable Employee: there the Impacted
              Employee is just whoever raised the ticket (Created By). Also on
              a Client Ticket Type, which is about its Client and Contact. */}
          {!ticket.ApplicableUserID && !ticket.IsClientTicket && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Impacted Employee</Typography.Text>
              <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{ticket.AgentName || '—'}</div>
              {/* Off: they get none of this ticket's alerts or emails (set on
                  an offboard's follow-up). Queue members can switch it. */}
              <div style={{ fontSize: 12, marginTop: 2 }}>
                {canChange ? (
                  <Checkbox checked={!!ticket.NotifyImpacted} onChange={(e) => toggleNotifyImpacted(e.target.checked)}>
                    Notify the Impacted Employee
                  </Checkbox>
                ) : !ticket.NotifyImpacted && (
                  <Typography.Text type="secondary">Not notified about this ticket</Typography.Text>
                )}
              </div>
            </div>
          )}
          {!ticket.IsClientTicket && (
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{ticket.ApplicableUserID ? 'Team' : 'Impacted Team'}</Typography.Text>
            <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{ticket.AgentTeamName || '—'}</div>
          </div>
          )}
          {/* Client Ticket Types: who it is for on the client's side. */}
          {ticket.ClientID && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Client</Typography.Text>
              <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{ticket.ClientName || '—'}</div>
            </div>
          )}
          {ticket.ClientID && ticket.ClientContactID && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Client Contact</Typography.Text>
              <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{ticket.ClientContactName || '—'}</div>
            </div>
          )}
          {ticket.ClientID && ticket.CampaignID && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Campaign</Typography.Text>
              <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{ticket.CampaignName || '—'}</div>
            </div>
          )}
        </div>

      </Card>

      {/* A client ticket: the client's other tickets, read when opened. */}
      {ticket.ClientID && (
        <Collapse
          size="small"
          style={{ marginBottom: 12, background: token.colorBgContainer }}
          activeKey={clientHistoryOpen}
          onChange={setClientHistoryOpen}
          items={[{
            key: 'client-history',
            label: counted('Client Ticket History', history),
            children: (
              <Table
                rowKey="ID"
                size="small"
                loading={history === null}
                dataSource={history || []}
                pagination={(history || []).length > 10 ? { pageSize: 10 } : false}
                locale={{ emptyText: 'No other tickets for this client.' }}
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Ticket', dataIndex: 'ID', key: 'ID', width: 100,
                    render: (v, r) => <Link to={`/helpdesk/${v}`}>{ticketKey(r)}</Link> },
                  { title: 'Subject', dataIndex: 'Subject', key: 'Subject', ellipsis: true },
                  { title: 'Request Type', dataIndex: 'FormName', key: 'FormName', width: 160, render: (v) => v || '—' },
                  { title: 'Client Contact', dataIndex: 'ClientContactName', key: 'ClientContactName', width: 160, render: (v) => v || '—' },
                  { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 130, render: (v) => v || '—' },
                  { title: 'Queue', dataIndex: 'QueueName', key: 'QueueName', width: 120, render: (v) => v || '—' },
                  { title: 'Status', dataIndex: 'Status', key: 'Status', width: 160,
                    render: (v) => <HdStatusTag status={v} /> },
                  { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 160, render: (v) => v || '—' },
                  { title: 'Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 160, render: (v) => formatTS(v) },
                ]}
              />
            ),
          }]}
        />
      )}

          <Card
            title="Description"
            size="small"
            style={{ marginBottom: 16 }}
            extra={editingDetails ? (
              <Space>
                <Button size="small" onClick={() => setEditingDetails(false)}>Cancel</Button>
                <Button size="small" type="primary" loading={detailsSaving} onClick={saveDetails}>Save</Button>
              </Space>
            ) : (
              canChange && <Button size="small" onClick={startEditDetails}>Edit</Button>
            )}
          >
            {editingDetails ? (
              <Form form={detailsForm} layout="vertical">
                <Form.Item label="Title" name="subject" rules={[{ required: true, message: 'Required' }]}>
                  <Input maxLength={500} />
                </Form.Item>
                <Form.Item label="Description" name="description" style={{ marginBottom: 0 }}>
                  <Input.TextArea autoSize={{ minRows: 3, maxRows: 16 }} />
                </Form.Item>
              </Form>
            ) : (
              <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {ticket.Description || <Typography.Text type="secondary">—</Typography.Text>}
              </Typography.Paragraph>
            )}
          </Card>

          {/* The queue's checklist: queue members only. */}
          {canChange && <TicketTasksCard ticketId={id} onChanged={loadComments} />}


          <Card title="Add a comment" size="small" style={{ marginBottom: 12 }}>
            <Segmented
              style={{ marginBottom: 8 }}
              value={commentMode || 'public'}
              onChange={setCommentMode}
              // Queue members: internal note or public comment. Anyone else:
              // public comments only. "Send email" is hidden while the Help
              // Desk comment alert does the emailing; the code behind it is
              // kept -- add { label: 'Send email', value: 'email' } back to
              // bring it back.
              options={ticket.SeesInternal ? [
                { label: 'Public comment', value: 'public' },
                { label: 'Internal note', value: 'internal' },
              ] : [{ label: 'Public comment', value: 'public' }]}
            />
            {commentMode === 'email' && (
              <Checkbox
                checked={includeSignature}
                onChange={(e) => setIncludeSignature(e.target.checked)}
                style={{ marginBottom: 6 }}
              >
                Include my signature in reply
              </Checkbox>
            )}
            {/* Public comment: who it notifies, from the same rules the post
                follows -- emailed, or Harbor alert only (and why). */}
            {(commentMode || 'public') === 'public' && notifyTo && (
              <div style={{ fontSize: 12, marginBottom: 8, lineHeight: 1.7 }}>
                <Typography.Text type="secondary">Notifies: </Typography.Text>
                {notifyTo.length === 0 ? <Typography.Text type="secondary">no one</Typography.Text>
                  : notifyTo.map((r, i) => (
                    <span key={`${r.name}${i}`}>
                      {i > 0 && ', '}
                      {r.emailed
                        ? <>{r.name} &lt;{r.email}&gt;</>
                        : <>{r.name} <Typography.Text type="secondary">(Harbor alert only: {r.reason})</Typography.Text></>}
                      {r.alert === 'customer replied' && <Typography.Text type="secondary"> (as &quot;customer replied&quot;)</Typography.Text>}
                    </span>
                  ))}
              </div>
            )}
            {commentMode === 'email' && emailTo && (
              <div style={{ fontSize: 12, marginBottom: 8, lineHeight: 1.7 }}>
                {!emailTo.enabled && (
                  <div><Typography.Text type="warning">Email is switched off: the comment will post, but nothing will be sent.</Typography.Text></div>
                )}
                <div>
                  <Typography.Text type="secondary">To: </Typography.Text>
                  {emailTo.to.length
                    ? emailTo.to.map((r) => `${r.name} <${r.email}>`).join(', ')
                    : <Typography.Text type="warning">no one</Typography.Text>}
                </div>
                {emailTo.cc.length > 0 && (
                  <div>
                    <Typography.Text type="secondary">Cc: </Typography.Text>
                    {emailTo.cc.map((r) => `${r.name} <${r.email}>`).join(', ')}
                  </div>
                )}
                {emailTo.skipped.length > 0 && (
                  <div>
                    <Typography.Text type="secondary">Not sent to: </Typography.Text>
                    {emailTo.skipped.map((r) => `${r.name} (${r.reason})`).join(', ')}
                  </div>
                )}
              </div>
            )}
            <CommentBox
              onSubmit={handleCommentSubmit}
              loading={addingComment}
              insertOptions={solutionOpts}
              insertLabel="Insert Solution"
              // Closed tickets take comments from the queue only (enforced on
              // the server too).
              disabled={!ticket.SeesInternal && isDoneStatus(ticket.Status)}
              placeholder={(commentMode || 'public') === 'internal'
                ? 'Internal note -- only the queue sees this...'
                : (commentMode === 'email'
                  ? 'Posted as a public comment and emailed to the impacted employee, watchers copied...'
                  : 'Public comment -- anyone who can view this ticket sees it...')}
            />
          </Card>

          <Card
            title="Comments"
            size="small"
            style={{ marginBottom: 12 }}
            extra={
              <Checkbox checked={hideSystem} onChange={(e) => toggleHideSystem(e.target.checked)}>
                Hide system comments
              </Checkbox>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {comments.length === 0 && (
                <Typography.Text type="secondary">No comments yet.</Typography.Text>
              )}
              {comments.map((c) => {
                const isSystem = !c.UserID || c.AuthorName === 'System';
                if (isSystem && hideSystem) return null;
                if (isSystem) {
                  return (
                    <div key={c.ID} style={{ borderLeft: '3px solid #f0f0f0', paddingLeft: 10 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 13, fontStyle: 'italic' }}>
                        <LinkifiedText text={c.Comment} />
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                        {formatTS(c.CreatedAt)}
                      </Typography.Text>
                    </div>
                  );
                }
                const initials = (c.AuthorName || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <div key={c.ID} style={{ display: 'flex', gap: 10 }}>
                    <Avatar size={28} icon={<UserOutlined />} style={{ flexShrink: 0 }}>
                      {initials}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Space size={8} style={{ marginBottom: 4 }}>
                        <Typography.Text strong style={{ fontSize: 14 }}>{c.AuthorName}</Typography.Text>
                        {/* who the ticket is for, at a glance among the queue's replies;
                            not on a Client Ticket Type (no Impacted Employee; Created
                            By already says who raised it) */}
                        {c.UserID === ticket.AgentUserID && !ticket.IsClientTicket && (
                          <Tag color="geekblue" icon={<UserOutlined />} style={{ marginInlineEnd: 0 }}>Impacted Employee</Tag>
                        )}
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
                        {c.Visibility === 'internal'
                          ? <Tag color="gold" style={{ marginInlineEnd: 0 }}>Internal</Tag>
                          : <Tag style={{ marginInlineEnd: 0 }}>Public</Tag>}
                        {c.EmailedTo && (
                          <Tooltip title={c.EmailedTo}><Tag color="blue" style={{ marginInlineEnd: 0 }}>Emailed</Tag></Tooltip>
                        )}
                      </Space>
                      <div style={{
                        background: c.Visibility === 'internal' ? '#fffbe6' : '#fafafa',
                        border: `1px solid ${c.Visibility === 'internal' ? '#ffe58f' : '#f0f0f0'}`,
                        borderRadius: 6, padding: '8px 12px', fontSize: 14,
                        whiteSpace: 'pre-wrap', lineHeight: 1.6,
                      }}>
                        <LinkifiedText text={c.Comment} />
                      </div>
                      {c.Attachments?.length > 0 && (
                        <CommentAttachments attachments={c.Attachments} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

          </Card>

          {/* Shipments and assets are about our employees' equipment: not on a
              Client Ticket Type. */}
          {!ticket.IsClientTicket && (
          <TicketAssetsCard
            ticketId={id}
            section="shipment"
            title="Shipments"
            extra={historyUserId && !ticket.ClientID ? (
              <Button type="link" size="small" style={{ padding: 0 }} onClick={showShipHistory}>
                Employee shipping history
              </Button>
            ) : null}
            rows={assets.filter((a) => a.Section === 'shipment')}
            assetTypes={assetTypes}
            onChanged={onAssetsChanged}
            readOnly={!canChange}
          />
          )}

          {!ticket.IsClientTicket && (
          <TicketAssetsCard
            ticketId={id}
            section="impacted"
            title="Impacted Assets"
            rows={assets.filter((a) => a.Section === 'impacted')}
            assetTypes={assetTypes}
            onChanged={onAssetsChanged}
            readOnly={!canChange}
          />
          )}
        </Col>

        {/* Right, one third: working the ticket, and what surrounds it */}
        <Col xs={24} md={10} lg={8}>
          <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { display: 'grid', gap: 18 } }}>
            <div>
              <div className="hd-field-label">Status</div>
            <Select
              value={ticket.Status}
              options={statusOptions(ticket)}
              popupMatchSelectWidth={false}   // room for the workflow's step labels
              onChange={handleStatusChange}
              disabled={!canChange}
              loading={statusSaving}
              style={{ width: '100%' }}
              size="small"
            />
            </div>
            <div>
              <div className="hd-field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Assigned To
                {inQueueTeam && ticket.AssignedToUserID !== currentUser?.ID && (
                  <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
                          onClick={() => handleAssign(currentUser.ID)} loading={assignSaving}>
                    Assign to Me
                  </Button>
                )}
              </div>
            <Select
              value={ticket.AssignedToUserID || undefined}
              options={assigneeOpts}
              onChange={handleAssign}
              loading={assignSaving}
              disabled={!ticket.QueueID || !canChange}
              placeholder={ticket.QueueID ? 'Unassigned' : 'No queue'}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              size="small"
            />
            </div>
            {/* Not on a Client Ticket Type: it is about its Client and Contact. */}
            {!ticket.IsClientTicket && (
            <div>
              <div className="hd-field-label">{aboutApplicable ? 'Applicable Employee' : 'Impacted Employee'}</div>
              <Select
                value={aboutId || undefined}
                options={impactedOpts}
                onChange={handleImpactedChange}
                loading={impactedSaving}
                disabled={!canChange}
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                size="small"
              />
            </div>
            )}
            {/* Due date: shown once one is set; the queue can add one. */}
            {ticket.DueDate || addingDue ? (
              <div>
                <div className="hd-field-label">Due Date</div>
                <DatePicker
                  value={ticket.DueDate ? dayjs(ticket.DueDate) : null}
                  onChange={(d) => { setAddingDue(false); handleDueDateChange(d); }}
                  disabled={!canChange}
                  loading={dueSaving}
                  size="small"
                  allowClear
                  autoFocus={addingDue}
                  defaultOpen={addingDue}
                  onOpenChange={(open) => { if (!open && !ticket.DueDate) setAddingDue(false); }}
                  style={{ width: '100%' }}
                />
              </div>
            ) : canChange && (
              <Button type="link" size="small" style={{ padding: 0, height: 'auto', justifySelf: 'start' }}
                      onClick={() => setAddingDue(true)}>
                + Add due date
              </Button>
            )}
          </Card>

          <Card title="Watchers" size="small" style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 10 }}>
              {watchers.length === 0 && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>No watchers yet.</Typography.Text>
              )}
              {watchers.map((w) => (
                <div key={w.ID} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Space size={6}>
                    <Avatar size={22} icon={<UserOutlined />} style={{ fontSize: 10 }} />
                    <Typography.Text style={{ fontSize: 13 }}>{w.DisplayName}</Typography.Text>
                  </Space>
                  {canChange && (
                    <Popconfirm
                      title="Remove watcher?"
                      onConfirm={() => handleRemoveWatcher(w.UserID)}
                      okText="Remove"
                      okType="danger"
                    >
                      <Button type="text" size="small" icon={<CloseOutlined />} danger />
                    </Popconfirm>
                  )}
                </div>
              ))}
            </div>
            {/* Anyone who can open the ticket can add a watcher (a client's
                own login: its company's logins); removing is the queue's. */}
            {(currentUser?.Role !== 'client' || !!ticket.IsClientTicket) && (
            <Space.Compact style={{ width: '100%' }}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Add watcher..."
                style={{ flex: 1 }}
                options={watcherOpts}
                value={watcherUserId}
                onChange={setWatcherUserId}
                size="small"
              />
              <Button
                size="small"
                type="primary"
                onClick={handleAddWatcher}
                loading={addingWatcher}
                disabled={!watcherUserId}
              >
                Add
              </Button>
            </Space.Compact>
            )}
          </Card>
          <Card
            title="Details"
            size="small"
            style={{ marginBottom: 12 }}
            // The Request Type's answers (client impacting, training related...)
            // head the panel; the queue edits them here.
            extra={questions.length > 0 && (editingAnswers ? (
              <Space>
                <Button size="small" onClick={() => setEditingAnswers(false)}>Cancel</Button>
                <Button size="small" type="primary" loading={answersSaving} onClick={saveAnswers}>Save</Button>
              </Space>
            ) : (
              canChange && <Button size="small" onClick={startEditAnswers}>Edit</Button>
            ))}
          >
            {questions.length > 0 && (
              <>
                {editingAnswers ? (
                  <Form form={answersForm} layout="vertical">
                    <HelpdeskFormQuestions formDef={formDef} />
                  </Form>
                ) : questions.map((q) => (
                  <DetailRow key={q.ID} label={q.QuestionText}
                             value={answerText(q, answers) != null
                               && <span style={{ whiteSpace: 'pre-wrap' }}>{answerText(q, answers)}</span>} />
                ))}
                <Divider style={{ margin: '8px 0' }} />
              </>
            )}
            {ticket.FormName && <DetailRow label="Request Type" value={ticket.FormName} />}
            <DetailRow label="Queue"       value={ticket.QueueName} />
            <DetailRow label="Assigned To" value={ticket.AssignedToName} />
            {(ticket.FirstResponseTargetAt || ticket.ResolutionTargetAt) && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                {ticket.FirstResponseTargetAt && (
                  <SLARow label="First Response Due" value={ticket.FirstResponseTargetAt} status={ticket.Status} />
                )}
                {ticket.ResolutionTargetAt && (
                  <SLARow label="Resolution Due" value={ticket.ResolutionTargetAt} status={ticket.Status} />
                )}
              </>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <DetailRow label="Created By"  value={ticket.CreatedByName} />
            <DetailRow label="Created After Hours" value={ticket.CreatedAfterHours ? 'Yes' : 'No'} />
            <DetailRow label="Created"     value={formatTS(ticket.CreatedAt)} />
            <DetailRow label="Updated"     value={formatTS(ticket.UpdatedAt)} />
            <DetailRow label="Status Updated" value={formatTS(ticket.StatusUpdatedAt)} />
            <DetailRow label="Closed"      value={ticket.ClosedAt ? formatTS(ticket.ClosedAt) : null} />
            <DetailRow label="Auto-Closed" value={ticket.AutoClosed ? 'Yes' : 'No'} />
          </Card>

          {ticket.ClientContactID && (
            <Card title="Client Contact" size="small" style={{ marginBottom: 12 }}>
              <DetailRow label="Name"  value={ticket.ClientContactName} />
              <DetailRow label="Title" value={ticket.ContactTitle} />
              <DetailRow label="Email" value={ticket.ContactEmail
                ? <a href={`mailto:${ticket.ContactEmail}`}>{ticket.ContactEmail}</a> : null} />
              <DetailRow label="Phone" value={ticket.ContactPhone} />
              <DetailRow label="Cell"  value={ticket.ContactCell} />
            </Card>
          )}


          {/* The employee's other tickets and their shipping history (not on a
              client ticket, which shows the client's history instead). Read
              when the panel is opened; each row opens its ticket. */}
          {ticket.AgentUserID && !ticket.ClientID && (
            <div ref={historyRef} style={{ marginBottom: 12 }}>
              <Collapse
                size="small"
                style={{ background: token.colorBgContainer }}
                activeKey={historyOpen}
                onChange={setHistoryOpen}
                items={[{
                  key: 'history',
                  label: `${who} Employee History`,
                  children: (
                    <>
                      <Segmented
                        size="small"
                        block
                        style={{ marginBottom: 8 }}
                        value={historyView}
                        onChange={setHistoryView}
                        options={[
                          { label: counted('Tickets', history), value: 'tickets' },
                          { label: counted('Shipments', shipHistory), value: 'shipments' },
                        ]}
                      />
                      {historyView === 'shipments' ? (
                        <Table
                          rowKey="ID"
                          size="small"
                          loading={shipHistory === null}
                          dataSource={shipHistory || []}
                          pagination={(shipHistory || []).length > 10 ? { pageSize: 10, size: 'small' } : false}
                          locale={{ emptyText: 'No shipments.' }}
                          columns={[
                            { title: 'Asset', dataIndex: 'AssetTypeName', key: 'AssetTypeName', ellipsis: true,
                              render: (v, r) => {
                                const name = `${v || 'Asset'}${r.Qty > 1 ? ` ×${r.Qty}` : ''}`;
                                return String(r.TicketID) === String(id)
                                  ? <span>{name} <Typography.Text type="secondary">(this ticket)</Typography.Text></span>
                                  : <Link to={`/helpdesk/${r.TicketID}`}>{name}</Link>;
                              } },
                            { title: 'Date', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 100, render: day },
                          ]}
                        />
                      ) : (
                        <Table
                          rowKey="ID"
                          size="small"
                          loading={history === null}
                          dataSource={history || []}
                          pagination={(history || []).length > 10 ? { pageSize: 10, size: 'small' } : false}
                          locale={{ emptyText: 'No other tickets.' }}
                          columns={[
                            { title: 'Subject', dataIndex: 'Subject', key: 'Subject', ellipsis: true,
                              render: (v, r) => <Link to={`/helpdesk/${r.ID}`}>{v || ticketKey(r)}</Link> },
                            { title: 'Request Type', dataIndex: 'FormName', key: 'FormName', ellipsis: true, render: (v) => v || '—' },
                            { title: 'Date', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 100, render: day },
                          ]}
                        />
                      )}
                    </>
                  ),
                }]}
              />
            </div>
          )}

          {(ticket.ParentTicket || ticket.FollowUps?.length > 0) && (
            <Card title="Linked Tickets" size="small" style={{ marginBottom: 12 }}>
              {ticket.ParentTicket && (
                <div style={{ marginBottom: ticket.FollowUps?.length ? 10 : 0 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Follows</Typography.Text>
                  <LinkedTicket t={ticket.ParentTicket} />
                </div>
              )}
              {ticket.FollowUps?.length > 0 && (
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Follow-ups</Typography.Text>
                  {ticket.FollowUps.map((f) => <LinkedTicket key={f.ID} t={f} />)}
                </div>
              )}
            </Card>
          )}

        </Col>
      </Row>

      <Modal open={closeOpen} title="Close this ticket?" okText="Close Ticket" cancelText="Keep It Open"
        confirmLoading={closing} onOk={handleCloseTicket} onCancel={() => setCloseOpen(false)} destroyOnClose>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          The ticket will be set to Completed. Add a comment if you like.
        </Typography.Paragraph>
        <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} maxLength={8000} value={closeNote}
          onChange={(e) => setCloseNote(e.target.value)} />
      </Modal>

      <HelpdeskCreateModal
        open={followUpOpen}
        onClose={() => setFollowUpOpen(false)}
        parentTicket={{ ID: ticket.ID, Subject: ticket.Subject, AgentUserID: ticket.AgentUserID, ApplicableUserID: ticket.ApplicableUserID }}
      />
    </div>
  );
}

function LinkedTicket({ t }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 4 }}>
      <Link to={`/helpdesk/${t.ID}`} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {ticketKey(t)}: {t.Subject}
      </Link>
      <HdStatusTag status={t.Status} style={{ margin: 0 }} />
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="hd-row">
      <Typography.Text type="secondary">{label}</Typography.Text>
      <Typography.Text>{value || '—'}</Typography.Text>
    </div>
  );
}

function SLARow({ label, value, status }) {
  const done    = isDoneStatus(status);
  const overdue = !done && value && dayjs(value).isBefore(dayjs());
  return (
    <div className="hd-row">
      <Typography.Text type="secondary">{label}</Typography.Text>
      <Typography.Text style={{ color: overdue ? '#ff4d4f' : undefined }}>
        {value ? formatTS(value) : '—'}
        {overdue && ' ⚠'}
      </Typography.Text>
    </div>
  );
}
